/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ConversationBubble.tsx - Componente burbuja flotante minimizada
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @description Burbuja flotante para conversaciones minimizadas con toggle IA/Humano
 * @version 1.0.1-pre.0.18
 * @architecture WASP Frontend
 * @optimization React.memo implementado para prevenir re-renders innecesarios
 * 
 * NUEVAS CARACTERÍSTICAS v1.0.1-pre.0.18:
 * - ✅ Botón toggle Modo IA / Modo Humano
 * - ✅ Respeta estado global copilotEnabled (3 switches sistema)
 * - ✅ Estados visuales: Deshabilitado / IA Activa / Modo Manual
 * - ✅ Integración con sistema de memoria (pause/resume endpoints)
 * - ✅ Gradientes y animaciones estilo ApplicationBubble
 * - ✅ Tooltips informativos según estado
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useRef, useEffect, memo } from 'react';
import { Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUser, faRobot, faBan } from '@fortawesome/free-solid-svg-icons';

interface ConversationBubbleProps {
  id: string;
  conversationId: string;
  displayName: string;
  unreadCount?: number;
  isActive?: boolean;
  position: { x: number; y: number };
  zIndex: number;
  onMaximize: () => void;
  onClose: () => void;
  onDrag?: (position: { x: number; y: number }) => void;
  onBringToFront?: () => void;
  
  // ══════════════════════════════════════════════════════════════════════════
  // NUEVAS PROPS - Sistema de Memoria y Estados IA
  // ══════════════════════════════════════════════════════════════════════════
  isPaused?: boolean;              // Estado pausa individual del usuario
  copilotEnabled?: boolean;        // Estado global IA del sistema (Switch 3)
  onTogglePause?: () => void;      // Callback para toggle pause/resume
  isTogglingPause?: boolean;       // Estado de loading durante toggle
}

/**
 * Componente ConversationBubble
 * 
 * Burbuja flotante minimizada que representa una conversación
 * 
 * @optimization Memoizado para evitar re-renders durante operaciones de lista
 * 
 * ESTADOS DEL BOTÓN IA/HUMANO:
 * 
 * 1. IA Sistema OFF (copilotEnabled = false):
 *    - Botón gris deshabilitado
 *    - Ícono: faBan
 *    - Tooltip: "IA desactivada en configuración del sistema"
 * 
 * 2. IA ON + Usuario NO pausado (isPaused = false):
 *    - Botón gradiente morado/azul
 *    - Ícono: faRobot
 *    - Tooltip: "Modo IA - Click para pausar"
 * 
 * 3. IA ON + Usuario pausado (isPaused = true):
 *    - Botón gradiente verde
 *    - Ícono: faUser
 *    - Tooltip: "Modo Humano - Click para reanudar IA"
 * 
 * @example
 * ```tsx
 * <ConversationBubble
 *   id="bubble-1"
 *   conversationId="56912345678"
 *   displayName="Jorge"
 *   unreadCount={3}
 *   isActive={true}
 *   position={{ x: 100, y: 100 }}
 *   zIndex={1000}
 *   isPaused={false}
 *   copilotEnabled={true}
 *   onMaximize={handleMaximize}
 *   onClose={handleClose}
 *   onTogglePause={handleTogglePause}
 * />
 * ```
 */
const ConversationBubbleComponent: React.FC<ConversationBubbleProps> = ({
  id,
  conversationId,
  displayName,
  unreadCount = 0,
  isActive = false,
  position,
  zIndex,
  onMaximize,
  onClose,
  onDrag,
  onBringToFront,
  isPaused = false,
  copilotEnabled = true,
  onTogglePause,
  isTogglingPause = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const bubbleRef = useRef<HTMLDivElement>(null);
  const dragStartTime = useRef<number>(0);

  /**
   * Obtener iniciales del nombre
   */
  const getInitials = (name: string): string => {
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  /**
   * Determinar estado del botón IA/Humano
   */
  const getAIButtonState = () => {
    if (!copilotEnabled) {
      return {
        background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
        icon: faBan,
        tooltip: 'IA desactivada en configuración del sistema',
        disabled: true
      };
    }

    if (isPaused) {
      return {
        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        icon: faUser,
        tooltip: 'Modo Humano - Click para reanudar IA',
        disabled: false
      };
    }

    return {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      icon: faRobot,
      tooltip: 'Modo IA - Click para pausar',
      disabled: false
    };
  };

  const aiButtonState = getAIButtonState();

  /**
   * Handler toggle pausa IA
   */
  const handleTogglePause = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (aiButtonState.disabled || isTogglingPause) {
      return;
    }

    if (onTogglePause) {
      onTogglePause();
    }
  };

  /**
   * Inicio de drag
   */
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // Solo click izquierdo

    e.preventDefault();
    e.stopPropagation();

    dragStartTime.current = Date.now();

    const rect = bubbleRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }

    setIsDragging(true);

    // Traer al frente
    if (onBringToFront) {
      onBringToFront();
    }
  };

  /**
   * Durante drag
   */
  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;

    e.preventDefault();

    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;

    // Límites de la pantalla
    const maxX = window.innerWidth - 60;
    const maxY = window.innerHeight - 60;

    const boundedX = Math.max(0, Math.min(newX, maxX));
    const boundedY = Math.max(0, Math.min(newY, maxY));

    if (onDrag) {
      onDrag({ x: boundedX, y: boundedY });
    }
  };

  /**
   * Fin de drag
   */
  const handleMouseUp = (e: MouseEvent) => {
    if (!isDragging) return;

    e.preventDefault();
    setIsDragging(false);

    const dragDuration = Date.now() - dragStartTime.current;
    const target = e.target as HTMLElement;
    const isButtonClick = target.closest('.bubble-close-btn, .bubble-ai-toggle');
  
    // Si el drag fue muy corto (< 200ms), considerarlo un click
    if (dragDuration < 200 && !isButtonClick) {
      onMaximize();
    }
  };

  /**
   * Event listeners para drag
   */
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  return (
    <div
      ref={bubbleRef}
      className={`conversation-bubble ${isDragging ? 'dragging' : ''}`}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        backgroundColor: isActive ? '#28a745' : '#007bff',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: isDragging ? 'grabbing' : 'grab',
        boxShadow: isDragging
          ? '0 8px 16px rgba(0,0,0,0.3)'
          : '0 4px 12px rgba(0,0,0,0.2)',
        zIndex,
        userSelect: 'none',
        transition: isDragging ? 'none' : 'all 0.2s ease',
        fontSize: '1.2rem',
        fontWeight: 'bold'
      }}
      onMouseDown={handleMouseDown}
      title={`${displayName}${unreadCount > 0 ? ` (${unreadCount} nuevos)` : ''}`}
    >
      {/* Avatar con iniciales */}
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.2)',
          position: 'relative'
        }}
      >
        {getInitials(displayName)}
      </div>

      {/* Badge de mensajes no leídos */}
      {unreadCount > 0 && (
        <Badge
          bg="danger"
          pill
          style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            fontSize: '0.7rem',
            minWidth: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #fff',
            animation: 'badgePulse 2s infinite'
          }}
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </Badge>
      )}

      {/* Indicador de activo */}
      {isActive && (
        <div
          style={{
            position: 'absolute',
            bottom: '2px',
            right: '2px',
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: '#fff',
            border: '2px solid #28a745',
            animation: 'activePulse 2s infinite'
          }}
          title="Conversación activa"
        />
      )}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* BOTÓN TOGGLE IA/HUMANO - NUEVO v1.0.1-pre.0.18 */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <button
        onClick={handleTogglePause}
        disabled={aiButtonState.disabled || isTogglingPause}
        style={{
          position: 'absolute',
          top: '-8px',
          right: '-8px',
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          background: aiButtonState.background,
          color: '#fff',
          border: '2px solid #fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: aiButtonState.disabled || isTogglingPause ? 'not-allowed' : 'pointer',
          fontSize: '0.75rem',
          opacity: 0,
          transition: 'all 0.3s ease',
          zIndex: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          animation: isTogglingPause ? 'spin 1s linear infinite' : 'none'
        }}
        className="bubble-ai-toggle"
        title={aiButtonState.tooltip}
      >
        <FontAwesomeIcon 
          icon={aiButtonState.icon} 
          style={{
            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))'
          }}
        />
      </button>

      {/* Botón cerrar - MOVIDO A LEFT */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        style={{
          position: 'absolute',
          top: '-8px',
          left: '-8px',  // ← CAMBIADO de right a left
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          backgroundColor: '#dc3545',
          color: '#fff',
          border: '2px solid #fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '0.7rem',
          opacity: 0,
          transition: 'opacity 0.2s ease',
          zIndex: 11,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
        }}
        className="bubble-close-btn"
        title="Cerrar"
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>

      <style>{`
        /* ═══════════════════════════════════════════════════════════════════ */
        /* ESTILOS EXISTENTES - PRESERVADOS */
        /* ═══════════════════════════════════════════════════════════════════ */
        
        .conversation-bubble {
          animation: bubbleAppear 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }

        @keyframes bubbleAppear {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .conversation-bubble:hover {
          transform: scale(1.05);
          box-shadow: 0 6px 16px rgba(0,0,0,0.25) !important;
        }

        /* FIX v1.0.1-pre.0.18: Mantener botones visibles con delay */
        .conversation-bubble:hover .bubble-close-btn,
        .conversation-bubble:hover .bubble-ai-toggle {
          opacity: 1;
        }
        
        .bubble-close-btn,
        .bubble-ai-toggle {
          transition: opacity 0.3s ease 0.2s !important; /* Delay de 200ms antes de desaparecer */
        }
        
        .bubble-close-btn:hover,
        .bubble-ai-toggle:hover {
          opacity: 1 !important;
          transition-delay: 0s !important; /* Sin delay al hacer hover directo */
        }

        .conversation-bubble.dragging {
          cursor: grabbing !important;
          opacity: 0.9;
        }

        @keyframes badgePulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }

        @keyframes activePulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        /* ═══════════════════════════════════════════════════════════════════ */
        /* NUEVOS ESTILOS - v1.0.1-pre.0.18 */
        /* ═══════════════════════════════════════════════════════════════════ */

        /* Animación de loading para toggle */
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* Hover mejorado para botón AI toggle */
        .bubble-ai-toggle:not(:disabled):hover {
          transform: scale(1.15);
          box-shadow: 0 4px 12px rgba(0,0,0,0.4) !important;
        }

        .bubble-ai-toggle:disabled {
          opacity: 0.6 !important;
          cursor: not-allowed !important;
        }

        /* Animación de fade-in estilo ApplicationBubble */
        @keyframes bubbleFadeIn {
          from {
            opacity: 0;
            transform: translateY(-5px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        /* Aplicar fade-in a botones al hacer hover */
        .conversation-bubble:hover .bubble-close-btn,
        .conversation-bubble:hover .bubble-ai-toggle {
          animation: bubbleFadeIn 0.2s ease-out;
        }

        /* ═══════════════════════════════════════════════════════════════════ */
        /* RESPONSIVE - PRESERVADO */
        /* ═══════════════════════════════════════════════════════════════════ */

        /* Responsive - Burbujas más grandes en móvil */
        @media (max-width: 768px) {
          .conversation-bubble {
            width: 70px !important;
            height: 70px !important;
            font-size: 1.3rem !important;
          }

          .bubble-close-btn {
            width: 28px !important;
            height: 28px !important;
            font-size: 0.9rem !important;
          }

          .bubble-ai-toggle {
            width: 32px !important;
            height: 32px !important;
            font-size: 0.85rem !important;
          }
        }

        /* Touch devices */
        @media (hover: none) and (pointer: coarse) {
          .conversation-bubble {
            cursor: pointer !important;
          }

          .conversation-bubble .bubble-close-btn,
          .conversation-bubble .bubble-ai-toggle {
            opacity: 1 !important;
          }
        }

        /* Prevenir selección de texto durante drag */
        .conversation-bubble.dragging,
        .conversation-bubble.dragging * {
          user-select: none !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
        }
      `}</style>
    </div>
  );
};

/**
 * Función de comparación personalizada para React.memo
 * Solo re-renderiza si cambian propiedades relevantes de la burbuja
 * 
 * ACTUALIZADA v1.0.1-pre.0.18: Agregadas comparaciones para isPaused, copilotEnabled
 */
const arePropsEqual = (
  prevProps: ConversationBubbleProps,
  nextProps: ConversationBubbleProps
): boolean => {
  return (
    prevProps.id === nextProps.id &&
    prevProps.conversationId === nextProps.conversationId &&
    prevProps.displayName === nextProps.displayName &&
    prevProps.unreadCount === nextProps.unreadCount &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.position.x === nextProps.position.x &&
    prevProps.position.y === nextProps.position.y &&
    prevProps.zIndex === nextProps.zIndex &&
    prevProps.isPaused === nextProps.isPaused &&
    prevProps.copilotEnabled === nextProps.copilotEnabled &&
    prevProps.isTogglingPause === nextProps.isTogglingPause
    // Las funciones callback no se comparan (referencias estables)
  );
};

/**
 * Exportar componente memoizado
 * Previene re-renders innecesarios durante operaciones de lista
 */
export const ConversationBubble = memo(ConversationBubbleComponent, arePropsEqual);

export default ConversationBubble;